import React, { useMemo, useContext, useEffect, type JSX } from 'react';
import MainGrid from './Components/MainGrid/MainGrid';
import StartedRoutines from './Components/Cards/StartedRoutines/StartedRoutinesPanel';
import TasksPanel from './Components/Cards/TasksPanel/TasksPanel';
import AccountStatus from './Components/Cards/AccountStatus/AccountStatus';
import Favorites from './Components/Cards/Favorites/Favorites';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EkonomiskAnalysCard from './Components/Cards/EkonomiskAnalysCard/EkonomiskAnalysCard';
import ErrorBoundary from '../../Components/ErrorBoundary/ErrorBoundary';
import BakgrundsjobbPanel from './Components//Cards/BakgrundsjobbPanel/BakgrundsjobbPanel';
import backgroundImage from '../../Assets/Backgrounds/mountain.svg';
import { LoginStateContext } from '../Login/Contexts/LoginStateContext/LoginStateContext';
import { Navigate } from 'react-router';
import {
  currentRoutingVersion,
  encodeBaseParamValue,
  System,
  useBaseRouteValue,
} from '../Routing/Utils/routerUtils';
import NoSessionBackground from '../Login/Components/NoSessionBackground/NoSessionBackground';
import { setPageTitle } from '../../Utils/analyticsUtils';
import { useQuery } from '@apollo/client';
import {
  AvenySYSOptionQuery,
  AvenySYSOptionQueryVariables,
} from '../../Components/QueryComponents/AvenySYSOptionQuery/_QueryTypes/AvenySYSOptionQuery';
import { ActiveDatabaseContext } from '../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import optionQuery from '../../Components/QueryComponents/AvenySYSOptionQuery/AvenySYSOptionQuery.gql';
import NewStartPanel from '../Dashboard/NewStartPanel';

interface IProps {}

interface IStartPanels {
  default: JSX.Element[];
  ta: JSX.Element[];
  cr: JSX.Element[];
}

const useStyles = makeStyles()((theme: Theme) => ({
  bgContainer: {
    display: 'flex',
    flex: '1 1 auto',
    justifyContent: 'center',
    overflowY: 'inherit',
    width: '100%',
    backgroundImage: 'url("' + backgroundImage + '")',
    backgroundPosition: 'bottom center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    backgroundSize: '100%',
    backgroundColor: theme.customProperties.backgroundImageColor,
  },
  container: {
    flex: '0 1 auto',
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  cardContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5),
    },
  },
}));

const getStartPanels = (): IStartPanels => ({
  default: window.env.showDemoComponents
    ? [
        <StartedRoutines key={1} />,
        <TasksPanel key={2} />,
        <AccountStatus key={3} />,
        <Favorites key={4} />,
        <EkonomiskAnalysCard key={5} type="pie" />,
        <EkonomiskAnalysCard key={6} type="column" />,
        <BakgrundsjobbPanel key={7} />,
      ]
    : [<StartedRoutines key={1} />],
  ta: window.env.showDemoComponents
    ? [
        <StartedRoutines key={1} />,
        <TasksPanel key={2} />,
        <AccountStatus key={3} />,
      ]
    : [<StartedRoutines key={1} />],
  cr: window.env.showDemoComponents
    ? [
        <StartedRoutines key={1} />,
        <TasksPanel key={2} />,
        // <PieChartPanel key={3} title="Kostnader Församlingen" sign="cr" />,
        <EkonomiskAnalysCard key={4} type="pie" />,
      ]
    : [<StartedRoutines key={1} />],
});

/**
 * Startvyn som nås efter inloggning. Håller olika kort med användarspecifk information och genvägar.
 */
export const StartPanel: React.FC<React.PropsWithChildren<IProps>> = () => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const { data: useDashboardSysOption, loading: loadingDashboardSysOption } =
    useQuery<AvenySYSOptionQuery, AvenySYSOptionQueryVariables>(optionQuery, {
      fetchPolicy: 'cache-and-network',
      variables: {
        dbName: activeDatabaseContext?.activeDatabase ?? '',
        subSystemCode: 'SYS',
        optionKey: 'SYS_NA_USE_DASHBOARD',
      },
      skip: !activeDatabaseContext?.activeDatabase,
    });

  const startPanels = useMemo(getStartPanels, [window.env.showDemoComponents]);
  const loginState = useContext(LoginStateContext);
  const baseparams = useBaseRouteValue();
  const missingParams =
    (baseparams.database && baseparams.username && baseparams.system) ==
    undefined;
  const { classes } = useStyles();

  useEffect(() => {
    setPageTitle('', 'Start');
  }, []);

  let startPanelElements = startPanels.default;

  if (loginState.sign === 'TA') {
    startPanelElements = startPanels.ta;
  } else if (loginState.sign === 'CR') {
    startPanelElements = startPanels.cr;
  }

  const useDashboard =
    useDashboardSysOption?.db?.SYSOptions_All?.[0]?.OptionData === 'Y';

  return (
    <ErrorBoundary name="startpanel">
      {loginState.isLoggedIn ? (
        missingParams ? (
          <Navigate
            to={`/${currentRoutingVersion}/${
              System.Lokalt
            }/${encodeBaseParamValue(
              loginState.sign,
              'base64url'
            )}/${encodeBaseParamValue(
              loginState.databases.at(0)?.name,
              'none'
            )}`}
            replace
          />
        ) : (
          <div className={classes.bgContainer}>
            {useDashboard && !loadingDashboardSysOption && <NewStartPanel />}
            {!useDashboard && !loadingDashboardSysOption && (
              <div className={classes.container}>
                <MainGrid>
                  {startPanelElements.map((CardElement, index) => (
                    <div key={index} className={classes.cardContainer}>
                      {CardElement}
                    </div>
                  ))}
                </MainGrid>
              </div>
            )}
          </div>
        )
      ) : loginState.loading ? null : (
        <NoSessionBackground />
      )}
    </ErrorBoundary>
  );
};

/** @component */
export default StartPanel;
